import { Component, OnInit } from '@angular/core';
import {
  PoBreadcrumb,
  PoNotificationService,
  PoTableAction,
  PoTableColumn,
  PoTableColumnLabel
} from '@po-ui/ng-components';
import { Router } from '@angular/router';
import { RpaService } from '../../core/rpa/rpa.service';

export interface CertidaoTableRow {
  key: string;
  competencia: string;
  fileName: string;
  dataHoraProc: string;
  status: string;
  raw?: unknown;
}

const PAGE_SIZE = 50;

@Component({
  selector: 'app-certidoes',
  templateUrl: './certidoes.component.html',
  styleUrls: ['./certidoes.component.css'],
  standalone: false
})
export class CertidoesComponent implements OnInit {
  tableItems: CertidaoTableRow[] = [];
  loadingTable = false;
  loadingShowMore = false;
  moreItens = false;
  private page = 1;

  readonly statusLabels: PoTableColumnLabel[] = [
    { value: 'PROCESSANDO', color: 'color-11', label: 'Processando', textColor: 'white' },
    { value: 'CANCELADO', color: 'color-05', label: 'Cancelado', textColor: 'white' },
    { value: 'SUCCESS', color: 'color-10', label: 'Emitido', textColor: 'white' },
    { value: 'BLOQUED', color: 'color-07', label: 'Bloqueado', textColor: 'white' },
    { value: 'UNEXPECTED', color: 'color-08', label: 'Erro', textColor: 'white' },
    { value: 'INVALID_CNPJ', color: 'color-05', label: 'CNPJ Inválido', textColor: 'white' }
  ];

  readonly columns: PoTableColumn[] = [
    { property: 'key', label: 'Chave' },
    { property: 'competencia', label: 'Competência' },
    { property: 'fileName', label: 'Nome do Arquivo' },
    {
      property: 'dataHoraProc',
      label: 'Processado em',
      type: 'date',
      format: 'dd/MM/yyyy HH:mm'
    },
    {
      property: 'status',
      label: 'Status',
      type: 'label',
      labels: this.statusLabels
    }
  ];

  readonly tableActions: PoTableAction[] = [
    {
      label: 'Visualizar arquivo',
      action: this.visualizarArquivo.bind(this),
      icon: 'an an-eye',
      visible: (row: CertidaoTableRow) => this.podeVisualizarArquivo(row)
    },
    // Ação oculta: com 1 ação o PO UI renderiza link; com 2+ exibe o menu de três pontinhos (igual ao RPA).
    {
      label: '',
      action: () => undefined,
      visible: () => false
    }
  ];

  readonly breadcrumb: PoBreadcrumb = {
    items: [
      { label: 'Home', link: '/home' },
      { label: 'Certidões' },
      { label: 'Arquivos disponíveis' }
    ]
  };

  constructor(
    private readonly router: Router,
    private readonly rpaService: RpaService,
    private readonly poNotificationService: PoNotificationService
  ) {}

  ngOnInit(): void {
    this.carregarTabela(true);
  }

  atualizarTabela(): void {
    this.carregarTabela(true);
  }

  carregarMais(): void {
    this.carregarTabela(false);
  }

  irRelatorio(): void {
    this.router.navigate(['certidoes/relatorio']);
  }

  podeVisualizarArquivo(row: CertidaoTableRow): boolean {
    return !!this.rpaService.resolverObjectNameArquivo(row.raw ?? row);
  }

  visualizarArquivo(row: CertidaoTableRow): void {
    const objectName = this.rpaService.resolverObjectNameArquivo(row.raw ?? row);
    if (!objectName) {
      this.poNotificationService.warning('Arquivo ainda não disponível.');
      return;
    }
    this.rpaService.requestRpaUrlFile(objectName).subscribe({
      next: (response: unknown) => {
        const url = this.rpaService.extrairUrlAssinadaResposta(response);
        if (!url) {
          this.poNotificationService.error('A API não retornou a URL do arquivo.');
          return;
        }
        window.open(url, '_blank');
      },
      error: () => this.poNotificationService.error('Erro ao carregar o arquivo.')
    });
  }

  private carregarTabela(reset: boolean): void {
    if (this.loadingTable || this.loadingShowMore) return;

    if (reset) {
      this.page = 1;
      this.tableItems = [];
      this.moreItens = false;
      this.loadingTable = true;
    } else {
      if (!this.moreItens) return;
      this.loadingShowMore = true;
    }

    this.rpaService
      .requestRpaFilesFiltrado({ key: 'CND-', page: this.page, pageSize: PAGE_SIZE })
      .subscribe({
        next: (response) => {
          const pagina = this.extrairArquivos(response)
            .filter((f) => this.ehCertidao(f))
            .map((f) => this.toArquivoRow(f));

          this.tableItems = reset ? pagina : [...this.tableItems, ...pagina];
          this.moreItens = !!response.hasNext;
          this.page++;
          this.loadingTable = false;
          this.loadingShowMore = false;

          if (reset && this.tableItems.length === 0) {
            this.poNotificationService.information('Nenhuma certidão encontrada.');
          }
        },
        error: () => {
          this.loadingTable = false;
          this.loadingShowMore = false;
          if (reset) {
            this.tableItems = [];
            this.moreItens = false;
          }
          this.poNotificationService.error('Erro ao carregar arquivos de certidões.');
        }
      });
  }

  private ehCertidao(file: unknown): boolean {
    const key = String((file as { key?: string })?.key ?? '').toUpperCase();
    return key.startsWith('CND-');
  }

  private extrairArquivos(response: unknown): unknown[] {
    if (!response) return [];
    const r = response as { items?: unknown[]; data?: unknown[] };
    return r.items || r.data || (Array.isArray(response) ? response : []);
  }

  private toArquivoRow(file: unknown): CertidaoTableRow {
    const f = file as {
      key?: string;
      competencia?: string;
      fileName?: string;
      dataHoraProc?: string;
      dataHoraProcessamento?: string;
      status?: string;
    };
    return {
      key: String(f?.key ?? ''),
      competencia: String(f?.competencia ?? '—'),
      fileName: String(f?.fileName ?? ''),
      dataHoraProc: f?.dataHoraProc ?? f?.dataHoraProcessamento ?? '',
      status: String(f?.status ?? '').toUpperCase(),
      raw: file
    };
  }
}
